import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import type { ContentItem } from '../api/types';
import { useMutation } from '../lib/use-api';
import { ErrorNotice } from './Feedback';

/**
 * Sửa nhãn của một học liệu.
 *
 * Gửi CẢ TẬP nhãn (PUT), không gửi từng thay đổi: backend thay toàn bộ trong
 * một transaction, nên không có trạng thái dở dang khi người dùng vừa thêm vừa
 * bớt trong cùng một lần sửa.
 */
export function TagEditor({
  content,
  onClose,
  onSaved,
}: {
  content: ContentItem;
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  const { t } = useTranslation(['admin', 'common']);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [value, setValue] = useState(content.tags.join(', '));

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const save = useMutation<string[], { tags: string[] }>(
    useCallback(
      (tags: string[]) =>
        apiFetch<{ tags: string[] }>(`/api/admin/content/${content.contentId}/tags`, {
          method: 'PUT',
          body: { tags },
        }),
      [content.contentId],
    ),
  );

  const submit = async (): Promise<void> => {
    const tags = value
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
    const result = await save.run(tags);
    if (result) onSaved();
  };

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <h2>{t('library.editTags')}</h2>
      <p className="muted">{content.title}</p>

      <label className="field">
        <span>{t('library.column.tags')}</span>
        <input
          type="text"
          value={value}
          placeholder={t('library.tagsPlaceholder')}
          onChange={(event) => setValue(event.target.value)}
        />
      </label>

      {save.error ? <ErrorNotice error={save.error} /> : null}

      <footer className="dialog__footer">
        <button type="button" onClick={() => dialogRef.current?.close()}>
          {t('common:action.cancel')}
        </button>
        <button type="button" disabled={save.pending} onClick={() => void submit()}>
          {t('common:action.save')}
        </button>
      </footer>
    </dialog>
  );
}
